// /knowledge 戦略マップ（DCM）の追加アクション用の生成サービス。
//
// backend の POST /knowledge/products/{id}/strategy-map/generate を呼び、
// ペルソナ/セグメント/訴求/コピーの「中身（フィールド値）」を取得する。
// backend が AI 失敗時にテンプレートで必ず全項目を埋めて返すが、
// ネットワーク断など呼び出し自体が失敗した場合に備え、フロント側にもテンプレートを持つ。
// 生成した中身は builder で型付きオブジェクト（nanoid 付き）へ組み立てる。
import { nanoid } from 'nanoid';
import {
  APPEAL_TYPE_LABELS,
  AppealType,
  DCM_APPEAL_AXIS_OPTIONS,
  DcmAppeal,
  DcmCopy,
  DcmFunnelStageId,
  DcmSegmentMaster,
  DcmWho,
  FUNNEL_STAGE_OPTIONS,
} from '../../types/knowledge';
import { apiRequest } from './axiosConfig';

export type StrategyGenerateKind = 'persona' | 'segment' | 'appeal' | 'copy';
export type StrategyGenerateMode = 'auto' | 'manual';

export interface StrategyGenerateContext {
  segment_name?: string;
  funnel_stage_label?: string;
  persona_name?: string;
  appeal_name?: string;
  appeal_direction?: string;
  next_order?: number;
  /** 「使わない案を再生成」の複数件ループ用: 重複回避のため既存＋生成済みのコピー文を積んで渡す */
  existing_copies?: string[];
}

export interface AppealContent {
  name?: string;
  type?: string;
  appeal_axis?: string;
  hook?: string;
  direction?: string;
  copies?: string[];
}

export interface PersonaContent {
  who_name?: string;
  persona?: {
    name?: string;
    age?: string;
    tagline?: string;
    story?: string;
    situation?: string;
    desire?: string;
    anxiety?: string;
    decision_context?: string;
  };
  demo?: Record<string, string>;
  emotion_map?: Record<string, string>;
  value?: Record<string, string>;
  appeals?: AppealContent[];
}

export interface SegmentContent {
  segment_name?: string;
  definition?: string;
  motivation?: string;
  common_situation?: string;
  common_desire?: string;
  common_anxiety?: string;
  common_decision_context?: string;
}

export interface CopyContent {
  text?: string;
}

interface GenerateResponse<T> {
  kind: StrategyGenerateKind;
  source: 'ai' | 'template';
  data: T;
}

const VALID_APPEAL_TYPES = Object.keys(APPEAL_TYPE_LABELS) as AppealType[];

// ── 訴求 type / appeal_axis の正規化ヘルパー ──────────────────────────────
// 正本は types/knowledge.ts の APPEAL_TYPE_LABELS / DCM_APPEAL_AXIS_OPTIONS。
// backend の AppealFieldNormalizer と閾値・判定条件を揃える（単体生成AIが type欄に
// 軸/ラベルを、appeal_axis欄に文章を入れて返しても、表示バッジがズレないよう防御する）。
const stripAppealSuffix = (value: string): string => value.replace(/訴求$/, '');

// 日本語ラベル → slug 逆引き（「訴求」有無ゆらぎ許容）。
// 同一ラベルを持つ functional/feature は、軸リストを持つ feature を優先（定義順の後勝ち）。
const LABEL_TO_TYPE: Record<string, AppealType> = {};
(Object.entries(APPEAL_TYPE_LABELS) as Array<[AppealType, string]>).forEach(([slug, label]) => {
  LABEL_TO_TYPE[label] = slug;
  LABEL_TO_TYPE[stripAppealSuffix(label)] = slug;
});

// type連動の軸フォールバックと軸→タイプ逆引きに使う（functional/value はキーが無い）。
const AXIS_OPTIONS_BY_TYPE: Record<string, readonly string[]> = DCM_APPEAL_AXIS_OPTIONS;

// 軸文字列 → 所属タイプ（全タイプ横断）。
const AXIS_TO_TYPE: Record<string, AppealType> = {};
Object.entries(AXIS_OPTIONS_BY_TYPE).forEach(([type, axes]) => {
  axes.forEach((axis) => {
    AXIS_TO_TYPE[axis] = type as AppealType;
  });
});

const isKnownAxis = (axis: string): boolean => Object.prototype.hasOwnProperty.call(AXIS_TO_TYPE, axis);

// 訴求軸として不正（軸リストにも無く文章とみなせる）か。
// backend AppealFieldNormalizer::looksLikeSentence と同一基準: 20文字超 / 「。」を含む / です・ます調で終わる。
const looksLikeSentence = (value: string): boolean =>
  Array.from(value).length > 20 || value.includes('。') || /(です|ます)$/.test(value);

// type連動の先頭軸（functional/value は AXIS_OPTIONS にキーが無いので feature 先頭で代替）。
const fallbackAxisForType = (type: AppealType): string =>
  AXIS_OPTIONS_BY_TYPE[type]?.[0] ?? DCM_APPEAL_AXIS_OPTIONS.feature[0];

const normalizeAppealType = (type?: string, axis?: string): AppealType => {
  const raw = (type ?? '').trim();
  // enum slug は大文字ゆらぎを吸収（例: 'Trust' → 'trust'）
  const lowered = raw.toLowerCase();
  if (lowered && (VALID_APPEAL_TYPES as string[]).includes(lowered)) {
    return lowered as AppealType;
  }
  // 日本語ラベル逆引き（「訴求」有無ゆらぎ許容）
  const byLabel = LABEL_TO_TYPE[raw] ?? LABEL_TO_TYPE[stripAppealSuffix(raw)];
  if (byLabel) {
    return byLabel;
  }
  // type欄に軸文字列が入っていたケース → その軸が属するタイプへ
  const byTypeAxis = AXIS_TO_TYPE[raw];
  if (byTypeAxis) {
    return byTypeAxis;
  }
  // type欄が空で appeal_axis が既知軸なら、その軸の所属タイプを採用する
  if (raw === '' && axis) {
    const byAppealAxis = AXIS_TO_TYPE[axis.trim()];
    if (byAppealAxis) {
      return byAppealAxis;
    }
  }
  return 'benefit';
};

// appeal_axis を決定する。既知の軸 or 短い名詞句は採用、無効（空/文章）なら
// content.type が軸文字列だった場合はそれを、無ければ type連動の先頭軸で補完する（backend と同ロジック）。
const resolveAppealAxis = (content: AppealContent, resolvedType: AppealType): string => {
  const rawAxis = (content.appeal_axis ?? '').trim();
  if (rawAxis && isKnownAxis(rawAxis)) {
    return rawAxis;
  }
  if (rawAxis && !looksLikeSentence(rawAxis)) {
    return rawAxis;
  }
  const rawType = (content.type ?? '').trim();
  if (rawType && AXIS_TO_TYPE[rawType]) {
    return rawType;
  }
  return fallbackAxisForType(resolvedType);
};

// ============================================================
// API 呼び出し（失敗時はフロント側テンプレートへフォールバック）
// ============================================================

const requestGenerate = async <T>(
  productId: number,
  kind: StrategyGenerateKind,
  mode: StrategyGenerateMode,
  instruction: string,
  context: StrategyGenerateContext,
  fallback: T,
): Promise<T> => {
  try {
    const res = await apiRequest.post<GenerateResponse<T>>(
      `knowledge/products/${productId}/strategy-map/generate`,
      { kind, mode, instruction, context },
    );
    // data が想定外（null/非オブジェクト）ならフォールバック
    if (res && res.data && typeof res.data === 'object') {
      return res.data;
    }
    return fallback;
  } catch (error) {
    return fallback;
  }
};

// ============================================================
// フロント側テンプレート（呼び出し自体が失敗した時の最終手段）
// ============================================================

const personaFallback = (instruction: string, context: StrategyGenerateContext): PersonaContent => {
  const segmentName = (context.segment_name || 'このセグメント').trim() || 'このセグメント';
  const stageLabel = (context.funnel_stage_label || 'ファネル').trim() || 'ファネル';
  const direction = instruction
    ? instruction
    : `${segmentName}の${stageLabel}に合わせ、課題認識と意思決定を前に進める。`;
  return {
    who_name: instruction ? instruction.slice(0, 24) : `${stageLabel}の${segmentName}`,
    persona: {
      name: '田村 さやか',
      age: '32歳',
      tagline: '都心在住・IT企業勤務',
      story: direction,
      situation: `${segmentName}として情報収集中。`,
      desire: '自分に合う選択肢を短時間で見極めたい。',
      anxiety: '失敗したくない、比較に時間をかけたくない。',
      decision_context: '信頼できる根拠と具体的な利用イメージがあると動きやすい。',
    },
    demo: {
      region: '都市部',
      gender: '問わず',
      occupation: 'オフィスワーカー',
      purchasing_power: '中〜高',
      tech_literacy: '中程度',
      life_stage: '働き盛り',
    },
    emotion_map: {
      pain_emotion: '今の方法では非効率だと感じている。',
      ideal_state: '納得感を持ってすぐ次の行動に進める。',
      blocking_emotion: '費用対効果や導入負担への不安。',
      decision_factor: '実績、わかりやすさ、導入後の成果。',
    },
    value: {
      functional_value: '必要な情報が整理され、比較しやすい。',
      economic_value: '検討や作業にかかる時間を削減できる。',
      emotional_value: '迷いが減り、安心して判断できる。',
      social_value: '周囲に説明しやすい選定理由が得られる。',
      summary: '判断の不安を減らし、次の行動を後押しする。',
    },
    // ペルソナ追加の自動生成訴求はちょうど3つに揃える（backend template と同じ3件構成）
    appeals: [
      {
        name: '納得感訴求',
        type: 'benefit',
        appeal_axis: '情緒的ベネフィット',
        direction,
        copies: [
          `${segmentName}の迷いを、次の一手に変える。`,
          '比較に迷う時間を減らし、納得して選べる。',
          '必要な根拠を整理して、判断を前に進める。',
        ],
      },
      {
        name: '効率訴求',
        type: 'functional',
        appeal_axis: '機能的ベネフィット',
        direction: `${segmentName}が今すぐ実感できる、時間と手間の削減。`,
        copies: [
          '面倒な手順を減らし、すぐに次へ進める。',
          '迷う時間を、成果につながる時間に変える。',
          '必要なことだけを、シンプルに。',
        ],
      },
      {
        name: '信頼訴求',
        type: 'trust',
        appeal_axis: '社会的証明',
        direction: `${segmentName}が安心して選べる、実績に基づく裏付け。`,
        copies: [
          `多くの${segmentName}に選ばれている理由がある。`,
          '実績が示す、確かな選択。',
          '安心して任せられる根拠がここに。',
        ],
      },
    ],
  };
};

const segmentFallback = (instruction: string, context: StrategyGenerateContext): SegmentContent => {
  const order = context.next_order ?? 0;
  return {
    segment_name: instruction ? instruction.split('\n')[0].slice(0, 32) : `新規セグメント${order + 1}`,
    definition: instruction || 'AIが提案した有望な顧客群。',
    motivation: '自分の状況に合う選択肢を早く見つけたい。',
    common_situation: '複数の選択肢を比較検討している。',
    common_desire: '自分に合う情報を早く見つけたい。',
    common_anxiety: '選定に失敗したくない。',
    common_decision_context: '信頼できる根拠があると動きやすい。',
  };
};

const appealFallback = (instruction: string, context: StrategyGenerateContext): AppealContent => {
  const personaName = (context.persona_name || 'このペルソナ').trim() || 'このペルソナ';
  const direction = instruction || `${personaName}の意思決定を後押しする切り口。`;
  return {
    name: instruction ? instruction.slice(0, 20) : '価値訴求',
    type: 'benefit',
    appeal_axis: '機能的ベネフィット',
    direction,
    copies: [
      `${personaName}の課題を、この一手で解決する。`,
      '必要な価値を、わかりやすく届ける。',
      '選ぶ理由が、はっきり伝わる。',
    ],
  };
};

const copyFallback = (context: StrategyGenerateContext): CopyContent => {
  const personaName = (context.persona_name || '').trim();
  return {
    text: personaName ? `${personaName}に、いま必要な価値をまっすぐ届ける。` : '別角度から刺す、新しいコピー案。',
  };
};

// ============================================================
// 公開 API: 中身の取得
// ============================================================

export const generatePersonaContent = (
  productId: number,
  mode: StrategyGenerateMode,
  instruction: string,
  context: StrategyGenerateContext,
): Promise<PersonaContent> =>
  requestGenerate<PersonaContent>(productId, 'persona', mode, instruction, context, personaFallback(instruction, context));

export const generateSegmentContent = (
  productId: number,
  mode: StrategyGenerateMode,
  instruction: string,
  context: StrategyGenerateContext,
): Promise<SegmentContent> =>
  requestGenerate<SegmentContent>(productId, 'segment', mode, instruction, context, segmentFallback(instruction, context));

export const generateAppealContent = (
  productId: number,
  mode: StrategyGenerateMode,
  instruction: string,
  context: StrategyGenerateContext,
): Promise<AppealContent> =>
  requestGenerate<AppealContent>(productId, 'appeal', mode, instruction, context, appealFallback(instruction, context));

export const generateCopyContent = (
  productId: number,
  context: StrategyGenerateContext,
): Promise<CopyContent> =>
  requestGenerate<CopyContent>(productId, 'copy', 'auto', '', context, copyFallback(context));

// ============================================================
// builder: 中身 → 型付きオブジェクト（nanoid 付与・全項目ガード）
// ============================================================

const buildAppeal = (content: AppealContent, order: number): DcmAppeal => {
  const copies: DcmCopy[] = (content.copies ?? [])
    .map((text) => String(text ?? '').trim())
    .filter(Boolean)
    .map((text) => ({ id: nanoid(), text, use: true }));
  if (copies.length === 0) {
    copies.push({ id: nanoid(), text: 'コピー案を入力してください。', use: true });
  }
  const type = normalizeAppealType(content.type, content.appeal_axis);
  const rawAxis = (content.appeal_axis ?? '').trim();
  const appealAxis = resolveAppealAxis(content, type);
  // 無効（文章）と判定して破棄した appeal_axis は、direction が空ならそこへ退避する
  // （AI の意図を捨てない。backend AppealFieldNormalizer と同挙動）。
  const rawDirection = content.direction?.trim() || '';
  const discardedSentenceAxis =
    rawAxis !== '' && appealAxis !== rawAxis && looksLikeSentence(rawAxis) ? rawAxis : '';
  return {
    appeal_id: nanoid(),
    name: content.name?.trim() || `訴求${order + 1}`,
    type,
    appeal_axis: appealAxis,
    hook: content.hook?.trim() || undefined,
    direction: rawDirection || discardedSentenceAxis,
    copies,
    order,
  };
};

export const buildAppealFromContent = (content: AppealContent, order: number): DcmAppeal =>
  buildAppeal(content, order);

export const buildWhoFromContent = (
  content: PersonaContent,
  segmentId: string,
  funnelStageId: DcmFunnelStageId,
): DcmWho => {
  const stage = FUNNEL_STAGE_OPTIONS.find((item) => item.value === funnelStageId);
  const appeals = (content.appeals?.length ? content.appeals : [{}]).map((appeal, index) =>
    buildAppeal(appeal, index),
  );
  return {
    who_id: `who-${nanoid()}`,
    segment_id: segmentId,
    funnel_stage_id: funnelStageId,
    who_name: content.who_name?.trim() || `${stage?.label || 'ファネル'}のペルソナ`,
    demo: {
      gender: content.demo?.gender || '',
      age_range: content.demo?.age_range || '',
      region: content.demo?.region || '',
      occupation: content.demo?.occupation || '',
      purchasing_power: content.demo?.purchasing_power || '',
      tech_literacy: content.demo?.tech_literacy || '',
      life_stage: content.demo?.life_stage || '',
    },
    persona: {
      name: content.persona?.name || '',
      age: content.persona?.age || '',
      tagline: content.persona?.tagline || '',
      story: content.persona?.story || '',
      situation: content.persona?.situation || '',
      desire: content.persona?.desire || '',
      anxiety: content.persona?.anxiety || '',
      decision_context: content.persona?.decision_context || '',
    },
    emotion_map: {
      pain_emotion: content.emotion_map?.pain_emotion || '',
      ideal_state: content.emotion_map?.ideal_state || '',
      blocking_emotion: content.emotion_map?.blocking_emotion || '',
      decision_factor: content.emotion_map?.decision_factor || '',
    },
    value: {
      functional_value: content.value?.functional_value || '',
      economic_value: content.value?.economic_value || '',
      emotional_value: content.value?.emotional_value || '',
      social_value: content.value?.social_value || '',
      summary: content.value?.summary || '',
    },
    appeals,
    order: Date.now(),
  };
};

export const buildSegmentFromContent = (content: SegmentContent, order: number): DcmSegmentMaster => ({
  segment_id: `segment-${nanoid()}`,
  segment_name: content.segment_name?.trim() || `新規セグメント${order + 1}`,
  definition: content.definition?.trim() || 'AIが提案した顧客群。',
  motivation: content.motivation?.trim() || '',
  common_situation: content.common_situation?.trim() || '',
  common_desire: content.common_desire?.trim() || '',
  common_anxiety: content.common_anxiety?.trim() || '',
  common_decision_context: content.common_decision_context?.trim() || '',
  order,
});
